import {
  OrderSummary,
  AddressSection,
  OrderItems,
  TransportationSection,
} from './sections';
import { useGetCartById, usePlaceOrder } from '~/api';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { BackButton } from '~/components/ui';
import { useSelector } from 'react-redux';
import type { RootState } from '~/redux/store';
import type { Location } from '~/types';
import type { CartItem } from './types';

export function Checkout() {
  const navigate = useNavigate();
  const { mutate: placeOrder, isPending: isPlacingOrder } = usePlaceOrder();
  const { t } = useTranslation('cart');
  const { id: cartId, addressId } = useParams<{
    id: string;
    addressId: string;
  }>();

  const user = useSelector((state: RootState) => state.auth.user);
  const locations = user?.companies?.map((company) => company.locations).flat();
  const selectedAddress = locations?.find((loc) => loc.id === addressId);

  const { data: cartData, isLoading } = useGetCartById(cartId!);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!cartData?.cart?.items) {
    return <div>Cart not found</div>;
  }

  const cartItems = cartData.cart.items as CartItem[];

  const handlePlaceOrder = () => {
    placeOrder(
      {
        orderId: cartId!,
        locationId: addressId!,
      },
      {
        onSuccess: () => {
          toast.success(t('cart.order_placed_success'));
          navigate(`/orders/view/${cartId}`);
        },
      }
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <BackButton link={'/cart'} label={t('cart.back_button_label')} />
        <h1 className="text-2xl sm:text-3xl text-neut-prim mb-2">
          {t('cart.checkout_title')}
        </h1>
        <p className="text-sm text-neut-sec">
          {t('cart.checkout_description')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <AddressSection
            address={selectedAddress as Location}
            title={t('cart.address_section_title')}
          />
          <TransportationSection items={cartItems} />
          <OrderItems
            title={t('cart.order_items_section_title')}
            items={cartItems}
          />
        </div>
        <div className="lg:col-span-1">
          <OrderSummary
            items={cartItems}
            supplierName={cartData.cart.sellingOrganization.name}
            isPlacingOrder={isPlacingOrder}
            onPlaceOrder={handlePlaceOrder}
          />
        </div>
      </div>
    </div>
  );
}
